// WS0 task 18: parity-test tooling in the legacy header, mirroring next-frontend's
// components/common/header/{ws0-switch,report-bug}.tsx.
//   - "Open in new JIFI": the same page in next-frontend, same tab (hidden when REACT_APP_NEXT_URL
//     is unset). With no equivalent page it lands on next's home, which says why.
//   - The one-off notice next's switch leaves in the URL when it had no equivalent page here.
//   - "Report a bug": posts to the backend's POST /bug-reports, which stamps the reporter from
//     the JWT and forwards to Slack.
// Deleted with frontend/ by task 17.

import React, { useEffect, useState } from 'react';
import superagent from 'superagent';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Label } from '@shadcn/components/ui/label';
import { Textarea } from '@shadcn/components/ui/textarea';
import { backendUrl } from '../../helpers/consts';
import { buildSwitchHref, readSwitchNotice, resolveSwitchTarget } from '../../helpers/ws0-switch';
import routeMap from '../../generated/route-map.json';

// Set at build time (see frontend/README.md). Never hardcode the other app's origin.
// Only an absolute http(s) URL: anything else (a value without a scheme, or SecureString
// ciphertext baked in by a missing --with-decryption) would resolve as a path on this site.
const rawNextUrl = (process.env.REACT_APP_NEXT_URL || '').trim().replace(/\/+$/, '');
const nextUrl = /^https?:\/\/[^/\s]+/i.test(rawNextUrl) ? rawNextUrl : '';
const buildSha = process.env.REACT_APP_BUILD_SHA || 'unknown';

// Matches the backend DTO's cap (BUG_REPORT_TEXT_MAX).
const TEXT_MAX = 2000;

export const Ws0Switch = () => {
  if (!nextUrl) return null;

  const openInNext = () => {
    const { pathname, search, hash } = window.location;
    const target = resolveSwitchTarget(routeMap, { pathname, search, hash });
    window.location.assign(buildSwitchHref(nextUrl, target, pathname));
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="mx-1"
      onClick={openInNext}
      title="Open this page in new JIFI"
    >
      Open in new JIFI
    </Button>
  );
};

export const Ws0SwitchNotice = () => {
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const notice = readSwitchNotice(window.location.search);
    if (!notice.message) return;
    setMessage(notice.message);
    const { pathname, hash } = window.location;
    window.history.replaceState(window.history.state, '', `${pathname}${notice.search ? `?${notice.search}` : ''}${hash}`);
  }, []);

  if (!message) return null;

  return (
    <div
      role="status"
      className="fixed left-1/2 top-[76px] z-[1100] flex -translate-x-1/2 items-center gap-3 rounded-md border bg-white px-4 py-2 text-sm text-black shadow"
    >
      <span>{message}</span>
      <button type="button" aria-label="Dismiss" className="opacity-60" onClick={() => setMessage(null)}>
        ×
      </button>
    </div>
  );
};

export const ReportBug = () => {
  const [open, setOpen] = useState(false);
  const [whatHappened, setWhatHappened] = useState('');
  const [whatDoing, setWhatDoing] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (value) => {
    setOpen(value);
    if (value) setResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!whatHappened.trim()) return;
    setSending(true);
    setResult(null);
    try {
      await superagent
        .post(`${backendUrl}/bug-reports`)
        .send({
          whatHappened: whatHappened.trim(),
          ...(whatDoing.trim() ? { whatDoing: whatDoing.trim() } : {}),
          // Matches the backend DTO cap (BUG_REPORT_ROUTE_MAX).
          route: window.location.href.slice(0, 8000),
          app: 'legacy',
          build: buildSha,
          userAgent: navigator.userAgent,
        })
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('Accept', 'application/json');
      setWhatHappened('');
      setWhatDoing('');
      setOpen(false);
      setResult({ ok: true, text: 'Thanks, your bug report was sent.' });
      setTimeout(() => setResult((current) => (current?.ok ? null : current)), 5000);
    } catch (error) {
      const message = error?.response?.body?.message;
      setResult({
        ok: false,
        text: (Array.isArray(message) ? message.join('; ') : message) || 'Could not send the bug report. Please try again.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" className="mx-1" onClick={() => toggle(true)}>
        Report a bug
      </Button>
      {result?.ok && !open && (
        <div role="status" className="fixed right-4 top-[76px] z-[1100] rounded-md border bg-white px-4 py-2 text-sm text-black shadow">
          {result.text}{' '}
          <button type="button" aria-label="Dismiss" className="opacity-60" onClick={() => setResult(null)}>
            ×
          </button>
        </div>
      )}
      <Dialog open={open} onOpenChange={toggle}>
        <DialogContent>
          <form onSubmit={submit} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Report a bug</DialogTitle>
              <DialogDescription>
                Sent to the JIFI team with this page's address, so there is no need to include it.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2">
              <Label htmlFor="bug-what-happened">What happened?</Label>
              <Textarea
                id="bug-what-happened"
                required
                maxLength={TEXT_MAX}
                value={whatHappened}
                onChange={(e) => setWhatHappened(e.target.value)}
                placeholder="What did you expect, and what did you see instead?"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="bug-what-doing">What were you doing? (optional)</Label>
              <Textarea
                id="bug-what-doing"
                maxLength={TEXT_MAX}
                value={whatDoing}
                onChange={(e) => setWhatDoing(e.target.value)}
                placeholder="e.g. picking order #GETJULY1234"
              />
            </div>
            {result && !result.ok && <p className="text-sm text-red-600">{result.text}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => toggle(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={sending || !whatHappened.trim()}>
                {sending ? 'Sending…' : 'Send report'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
